import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Mail, Linkedin, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Users, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ListBuilderContact } from '@/hooks/useListBuilder';
import { openExternal } from '@/lib/utils';
import { ContactNameLink } from '@/components/contacts/ContactNameLink';

interface ListBuilderResultsTableProps {
  contacts: ListBuilderContact[];
  totalCount: number;
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  pageSize: number;
  pageIndex: number;
  onPageSizeChange: (size: number) => void;
  onPageIndexChange: (index: number) => void;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onContactClick?: (contactId: string) => void;
}

const seniorityColor: Record<string, string> = {
  'C-Suite': 'bg-red-100 text-red-800',
  'VP': 'bg-purple-100 text-purple-800',
  'Director': 'bg-blue-100 text-blue-800',
  'Manager': 'bg-green-100 text-green-800',
  'Senior': 'bg-teal-100 text-teal-800',
  'Mid-Level': 'bg-emerald-100 text-emerald-800',
  'Individual Contributor': 'bg-gray-100 text-gray-700',
  'Entry-Level': 'bg-amber-100 text-amber-800',
  'Other': 'bg-gray-100 text-gray-600',
};

export function ListBuilderResultsTable({
  contacts,
  totalCount,
  isLoading,
  selectedIds,
  onSelectionChange,
  pageSize,
  pageIndex,
  onPageSizeChange,
  onPageIndexChange,
  sortField,
  sortDir,
  onSort,
}: ListBuilderResultsTableProps) {
  const totalPages = Math.ceil(totalCount / pageSize);

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
    return sortDir === 'desc'
      ? <ArrowDown className="h-3 w-3 ml-1 inline" />
      : <ArrowUp className="h-3 w-3 ml-1 inline" />;
  };
  const sortableProps = (field: string) =>
    onSort
      ? { className: 'cursor-pointer select-none', onClick: () => onSort(field) }
      : {};
  const allPageSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const somePageSelected = contacts.some((c) => selectedIds.has(c.id));

  const handleSelectAll = (checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      contacts.forEach((c) => newSet.add(c.id));
    } else {
      contacts.forEach((c) => newSet.delete(c.id));
    }
    onSelectionChange(newSet);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    onSelectionChange(newSet);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Users className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">No contacts found</p>
        <p className="text-sm mt-1">Try adjusting your filters or import contacts first</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) {
                      (el as any).indeterminate = somePageSelected && !allPageSelected;
                    }
                  }}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead {...sortableProps('name')}>Name{onSort && renderSortIcon('name')}</TableHead>
              <TableHead {...sortableProps('title')}>Title{onSort && renderSortIcon('title')}</TableHead>
              <TableHead {...sortableProps('company')}>Company{onSort && renderSortIcon('company')}</TableHead>
              <TableHead>Seniority</TableHead>
              <TableHead className="text-center">Email</TableHead>
              <TableHead className="text-center">LinkedIn</TableHead>
              <TableHead {...sortableProps('category')}>Category{onSort && renderSortIcon('category')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                className={selectedIds.has(contact.id) ? 'bg-primary/5' : ''}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(contact.id)}
                    onCheckedChange={(checked) =>
                      handleSelectOne(contact.id, checked === true)
                    }
                  />
                </TableCell>
                <TableCell className="font-medium max-w-[180px] truncate" title={contact.full_name || ''}>
                  {/* Canonical: opens the shared read-only modal via the provider
                      (contact_modal.md §3). `onContactClick` kept for legacy callers. */}
                  <ContactNameLink
                    contactId={contact.id}
                    name={contact.full_name || '-'}
                    fallbackData={{
                      full_name: contact.full_name,
                      job_title: contact.job_title,
                      company_name: contact.company_name,
                      email: contact.email,
                      linkedin_url: contact.linkedin_url,
                    }}
                  />
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-sm" title={contact.job_title || ''}>
                  {contact.job_title || '-'}
                </TableCell>
                <TableCell className="max-w-[160px] truncate text-sm" title={contact.company_name || ''}>
                  {contact.company_name || '-'}
                </TableCell>
                <TableCell>
                  {contact.seniority_level ? (
                    <Badge
                      variant="secondary"
                      className={`text-xs ${seniorityColor[contact.seniority_level] || 'bg-gray-100 text-gray-600'}`}
                    >
                      {contact.seniority_level.replace('-Level', '')}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {contact.email ? (
                    <Mail className="h-4 w-4 text-emerald-600 mx-auto" />
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {contact.linkedin_url ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openExternal(contact.linkedin_url); }}
                      className="inline-flex"
                    >
                      <Linkedin className="h-4 w-4 text-blue-600 mx-auto hover:text-blue-800 transition-colors" />
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {contact.company_category || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span>
            {pageIndex * pageSize + 1}-{Math.min((pageIndex + 1) * pageSize, totalCount)} of{' '}
            {totalCount.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={pageIndex === 0} onClick={() => onPageIndexChange(0)}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={pageIndex === 0} onClick={() => onPageIndexChange(pageIndex - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm px-2">Page {pageIndex + 1} of {totalPages || 1}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={pageIndex >= totalPages - 1} onClick={() => onPageIndexChange(pageIndex + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={pageIndex >= totalPages - 1} onClick={() => onPageIndexChange(totalPages - 1)}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
